/**
 * FE#238 — decide whether a send retry is a PHANTOM that must be dropped.
 *
 * The extension re-attempts/resends messageIds whose `usermessages` docs no
 * longer exist (verified live: 0 docs), writing `before-wa-send` rows that can
 * never confirm and surfacing as false red errors / "resend does nothing". The
 * pre-existing guard drops only when the INCOMING `userMessage.deleted` flag is
 * set — but a stale snapshot from an MQTT/FCM retry payload has no such flag, so
 * the phantom slips through.
 *
 * The authoritative signal is the BACKEND: does a live (non-deleted) doc exist
 * for this id? The local store can't be trusted for absence — it is a paginated
 * subset, so a legit retry for a not-currently-loaded message would be wrongly
 * dropped. Hence the caller does a backend existence check and passes its result
 * here. FAIL OPEN: when the check is unavailable (null/undefined — e.g. the fetch
 * errored) we do NOT drop, so a transient blip never kills a legitimate retry.
 *
 * Pure so the decision is unit-testable without a store, a clock, or the network.
 */

export interface RetryLivenessInput {
  /** The `.deleted` flag on the incoming (possibly stale) userMessage. */
  incomingDeleted?: boolean;
  /**
   * Result of the authoritative backend existence check for this id:
   *  - an array of the live docs found (`[]` = confirmed gone, 0 docs);
   *  - `null`/`undefined` = the check was unavailable (fail-open, do not drop).
   */
  backendDocs?: Array<{ deleted?: boolean }> | null;
}

export const shouldDropPhantomRetry = ({ incomingDeleted, backendDocs }: RetryLivenessInput): boolean => {
  // The one signal we already trust locally always wins.
  if (incomingDeleted) return true;
  // Fail open: an unavailable backend check must never drop a legit retry.
  if (backendDocs == null) return false;
  // Confirmed gone (0 docs) => phantom.
  if (backendDocs.length === 0) return true;
  // The backend still has the doc but it's deleted => phantom.
  return backendDocs[0]?.deleted === true;
};
